import { createContext, useContext, useMemo, useState, ReactNode } from 'react'

// Types pour le storage (vous pouvez adapter avec vos enums)
export enum StorageKey {
  USER_PREFERENCES = 'user_preferences',
  THEME = 'theme',
  LANGUAGE = 'language',
  AUTH_TOKEN = 'auth_token',
  TEMP_DATA = 'temp_data',
  // Ajoutez vos clés spécifiques ici
}

type IStorage = {
  [key in StorageKey]?: unknown
}

interface IStorageContext {
  storage: IStorage
  setStorage: (key: StorageKey | string, value: unknown) => void
  delStorage: (key: StorageKey | string) => void
  getValue: <T = unknown>(key: StorageKey | string) => T | undefined
  getValueAndDelete: <T = unknown>(key: StorageKey | string) => T | undefined
}

interface StorageProviderProps {
  children: ReactNode
}

const StorageContext = createContext<IStorageContext>(null!)

export function useStorage() {
  return useContext(StorageContext)
}

export const StorageProvider: React.FC<StorageProviderProps> = ({ children }) => {
  const [storage, _setStorage] = useState<IStorage>({})

  const setStorage = (k: StorageKey | string, v: unknown) => {
    _setStorage((s) => ({ ...s, [k]: v }))
  }

  const delStorage = (k: StorageKey | string) => {
    _setStorage((s) => {
      const newStorage = { ...s }
      delete newStorage[k as StorageKey]
      return newStorage
    })
  }

  const getValue = <T = unknown>(k: StorageKey | string): T | undefined => {
    return storage[k as StorageKey] as T | undefined
  }

  const getValueAndDelete = <T = unknown>(k: StorageKey | string): T | undefined => {
    const v = storage[k as StorageKey] as T | undefined
    delStorage(k)
    return v
  }

  const value: IStorageContext = useMemo(() => ({
    storage,
    setStorage,
    delStorage,
    getValueAndDelete,
    getValue
  }), [storage])

  return <StorageContext.Provider value={value}>{children}</StorageContext.Provider>
}
